import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getProjects, getUploads } from '../../services/api'
import { Project, Upload } from '../../types'
import { FolderOpen, Upload as UploadIcon, MapPin, Clock, AlertCircle, Building2, User } from 'lucide-react'

export default function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([])
  const [uploads, setUploads] = useState<Upload[]>([])
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [currentClient, setCurrentClient] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const userStr = localStorage.getItem('dronemap_user')
    if (userStr) {
      const user = JSON.parse(userStr)
      setCurrentUser(user)
    }

    Promise.all([getProjects(), getUploads()])
      .then(([projRes, uploadRes]) => {
        setProjects(projRes.data.projects)
        setUploads(uploadRes.data.uploads)

        // If client user, extract client info from first project
        if (userStr) {
          const user = JSON.parse(userStr)
          if (user.client_id && projRes.data.projects.length > 0) {
            const clientProj = projRes.data.projects.find((p: any) => p.client_id === user.client_id)
            if (clientProj) {
              setCurrentClient({ name: clientProj.client_name, id: user.client_id })
            }
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const isInternal = currentUser && !currentUser.client_id
  const isClientUser = currentUser && currentUser.client_id

  const recentUploads = uploads.slice(0, 5)
  const pendingCount = uploads.filter(u => u.status === 'pending' || u.status === 'processing').length
  const completedCount = uploads.filter(u => u.status === 'completed').length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Conditional Header */}
      <div className="card bg-gradient-to-r from-navy-800 to-navy-700 text-white">
        <div className="flex items-center justify-between">
          <div>
            {isInternal ? (
              <>
                <h1 className="text-2xl font-bold">DroneScape Admin</h1>
                <p className="text-navy-400 mt-1">
                  Welcome back, {currentUser?.first_name} {currentUser?.last_name}
                </p>
              </>
            ) : isClientUser && currentClient ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <Building2 size={20} className="text-orange-500" />
                  <h1 className="text-2xl font-bold">{currentClient.name}</h1>
                </div>
                <div className="flex items-center gap-2 text-navy-400">
                  <User size={14} />
                  <span>{currentUser?.first_name} {currentUser?.last_name} ({currentUser?.email})</span>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold">Dashboard</h1>
                <p className="text-navy-400 mt-1">Welcome to DroneScape</p>
              </>
            )}
          </div>
          <div className="hidden sm:block">
            <div className="text-right">
              <p className="text-xs text-navy-400 uppercase tracking-wider">Role</p>
              <p className="text-sm font-medium text-orange-400 capitalize">
                {currentUser?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-navy-100 text-navy-700">
              <FolderOpen size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Projects</p>
              <p className="text-2xl font-bold text-navy-800">{projects.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-orange-100 text-orange-600">
              <UploadIcon size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Uploads</p>
              <p className="text-2xl font-bold text-navy-800">{uploads.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100 text-green-600">
              <MapPin size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-navy-800">{completedCount}</p>
              {pendingCount > 0 && (
                <p className="text-xs text-orange-500">{pendingCount} processing</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Uploads */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-navy-800">Recent Uploads</h3>
          <Link to="/projects" className="text-sm text-orange-500 hover:text-orange-600 font-medium">
            View All
          </Link>
        </div>

        {recentUploads.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <UploadIcon size={48} className="mx-auto mb-4 opacity-50" />
            <p>No uploads yet. Create a project and upload your first WebODM backup.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">File</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentUploads.map(upload => (
                  <tr key={upload.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded bg-navy-100 flex items-center justify-center mr-3">
                          <MapPin size={14} className="text-navy-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy-800 truncate max-w-xs">
                            {upload.original_filename || `Upload #${upload.id}`}
                          </p>
                          <p className="text-xs text-gray-400">
                            {upload.selected_indices?.join(', ') || 'Orthophoto only'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={upload.status} />
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock size={14} className="mr-1.5" />
                        {new Date(upload.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {upload.status === 'completed' && (
                        <Link
                          to={`/map/${upload.id}`}
                          className="text-sm text-orange-500 hover:text-orange-600 font-medium"
                        >
                          View Map
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Projects */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-navy-800">Projects</h3>
          <Link to="/projects" className="text-sm text-orange-500 hover:text-orange-600 font-medium">
            Manage Projects
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(project => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="block p-4 rounded-lg border border-gray-100 hover:border-orange-200 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-navy-800">{project.name}</h4>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>
                </div>
                <FolderOpen size={18} className="text-navy-400 flex-shrink-0 ml-2" />
              </div>
              {project.location_lat && project.location_lon && (
                <div className="flex items-center mt-3 text-xs text-gray-400">
                  <MapPin size={12} className="mr-1" />
                  {project.location_lat.toFixed(4)}, {project.location_lon.toFixed(4)}
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles = {
    pending: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    processing: 'bg-blue-50 text-blue-600 border-blue-200',
    completed: 'bg-green-50 text-green-600 border-green-200',
    failed: 'bg-red-50 text-red-600 border-red-200',
  }

  const icons = {
    pending: <Clock size={12} className="mr-1" />,
    processing: <div className="animate-spin rounded-full h-3 w-3 border-b border-current mr-1" />,
    completed: <MapPin size={12} className="mr-1" />,
    failed: <AlertCircle size={12} className="mr-1" />,
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status as keyof typeof styles]}`}>
      {icons[status as keyof typeof icons]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}
